/**
 * data/instrumentCatalog/options.ts
 * Options instrument reference catalog
 * 
 * IMPORTANT: Options contracts are ephemeral (0DTE, weekly, monthly expirations).
 * This catalog focuses on optionable underlying instruments rather than specific
 * option contracts. Actual option contracts (e.g., AAPL241220C00150000) are
 * handled via runtime option chain data.
 * 
 * Research file: docs/reference/InstrumentCatalogResearch/Options_DataPopulationResearch.md
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Options instrument catalog
 * 
 * Contains 170+ optionable instruments across:
 * - Index options (SPX, NDX, RUT, VIX)
 * - ETF options (broad market, sector, commodity, currency)
 * - Single stock options (liquid mega-caps)
 * - Futures options (equity index, rates, metals, energy, agriculture, FX, crypto)
 */
export const OPTIONS_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== OPTIONS - INDEX & VOLATILITY =====
  
  {
    id: 'SPX_OPTIONS',
    symbol: 'SPX',
    name: 'S&P 500 Index Options',
    shortName: 'SPX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; daily/weekly/monthly expirations'
  },
  
  {
    id: 'XSP_OPTIONS',
    symbol: 'XSP',
    name: 'Mini-SPX Index Options (1/10 SPX)',
    shortName: 'Mini-SPX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; smaller notional; daily/weekly/monthly'
  },
  
  {
    id: 'NDX_OPTIONS',
    symbol: 'NDX',
    name: 'Nasdaq-100 Index Options',
    shortName: 'NDX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; daily/weekly/monthly'
  },
  
  {
    id: 'XND_OPTIONS',
    symbol: 'XND',
    name: 'Mini-NDX Index Options (1/100 NDX)',
    shortName: 'Mini-NDX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; smaller notional; daily/weekly/monthly'
  },
  
  {
    id: 'RUT_OPTIONS',
    symbol: 'RUT',
    name: 'Russell 2000 Index Options',
    shortName: 'RUT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; weekly/monthly (select dailies)'
  },
  
  {
    id: 'DJX_OPTIONS',
    symbol: 'DJX',
    name: 'Dow Jones Industrial Average Options',
    shortName: 'DJX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; weekly/monthly'
  },
  
  {
    id: 'OEX_OPTIONS',
    symbol: 'OEX',
    name: 'S&P 100 Index Options (American)',
    shortName: 'OEX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, American-style; weekly/monthly'
  },
  
  {
    id: 'XEO_OPTIONS',
    symbol: 'XEO',
    name: 'S&P 100 Index Options (European)',
    shortName: 'XEO Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style'
  },
  
  {
    id: 'VIX_OPTIONS',
    symbol: 'VIX',
    name: 'Cboe Volatility Index Options',
    shortName: 'VIX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'volatility points',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Cash-settled, European-style; standard Weds expiries; non-equity style settlement'
  },
  
  // ===== OPTIONS - BROAD & SECTOR ETFs =====
  
  {
    id: 'SPY_OPTIONS',
    symbol: 'SPY',
    name: 'SPDR S&P 500 ETF Options',
    shortName: 'SPY Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'index-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: '0DTE/daily, weekly, monthly; physical settlement'
  },
  
  {
    id: 'QQQ_OPTIONS',
    symbol: 'QQQ',
    name: 'Invesco QQQ Trust Options',
    shortName: 'QQQ Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: '0DTE/daily, weekly, monthly; physical'
  },
  
  {
    id: 'IWM_OPTIONS',
    symbol: 'IWM',
    name: 'iShares Russell 2000 ETF Options',
    shortName: 'IWM Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Daily/weekly/monthly; physical'
  },
  
  {
    id: 'DIA_OPTIONS',
    symbol: 'DIA',
    name: 'SPDR Dow Jones Industrial Average Options',
    shortName: 'DIA Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'VOO_OPTIONS',
    symbol: 'VOO',
    name: 'Vanguard S&P 500 ETF Options',
    shortName: 'VOO Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'VTI_OPTIONS',
    symbol: 'VTI',
    name: 'Vanguard Total Stock Market ETF Options',
    shortName: 'VTI Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'IVV_OPTIONS',
    symbol: 'IVV',
    name: 'iShares Core S&P 500 ETF Options',
    shortName: 'IVV Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'SPLG_OPTIONS',
    symbol: 'SPLG',
    name: 'SPDR Portfolio S&P 500 ETF Options',
    shortName: 'SPLG Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'TLT_OPTIONS',
    symbol: 'TLT',
    name: 'iShares 20+ Year Treasury ETF Options',
    shortName: 'TLT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'IEF_OPTIONS',
    symbol: 'IEF',
    name: 'iShares 7-10 Year Treasury ETF Options',
    shortName: 'IEF Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'SHY_OPTIONS',
    symbol: 'SHY',
    name: 'iShares 1-3 Year Treasury ETF Options',
    shortName: 'SHY Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'HYG_OPTIONS',
    symbol: 'HYG',
    name: 'iShares High Yield Corporate Bond ETF Options',
    shortName: 'HYG Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'LQD_OPTIONS',
    symbol: 'LQD',
    name: 'iShares Investment Grade Corporate Bond ETF Options',
    shortName: 'LQD Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'IYR_OPTIONS',
    symbol: 'IYR',
    name: 'iShares U.S. Real Estate ETF Options',
    shortName: 'IYR Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  // Sector ETF Options
  {
    id: 'XLK_OPTIONS',
    symbol: 'XLK',
    name: 'Technology Select Sector SPDR Options',
    shortName: 'XLK Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLF_OPTIONS',
    symbol: 'XLF',
    name: 'Financial Select Sector SPDR Options',
    shortName: 'XLF Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLE_OPTIONS',
    symbol: 'XLE',
    name: 'Energy Select Sector SPDR Options',
    shortName: 'XLE Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLI_OPTIONS',
    symbol: 'XLI',
    name: 'Industrial Select Sector SPDR Options',
    shortName: 'XLI Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLY_OPTIONS',
    symbol: 'XLY',
    name: 'Consumer Discretionary Select Sector SPDR Options',
    shortName: 'XLY Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLP_OPTIONS',
    symbol: 'XLP',
    name: 'Consumer Staples Select Sector SPDR Options',
    shortName: 'XLP Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLV_OPTIONS',
    symbol: 'XLV',
    name: 'Health Care Select Sector SPDR Options',
    shortName: 'XLV Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLU_OPTIONS',
    symbol: 'XLU',
    name: 'Utilities Select Sector SPDR Options',
    shortName: 'XLU Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLC_OPTIONS',
    symbol: 'XLC',
    name: 'Communication Services Select Sector SPDR Options',
    shortName: 'XLC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'XLB_OPTIONS',
    symbol: 'XLB',
    name: 'Materials Select Sector SPDR Options',
    shortName: 'XLB Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  // ===== OPTIONS - COMMODITY & CURRENCY ETFs =====
  
  {
    id: 'GLD_OPTIONS',
    symbol: 'GLD',
    name: 'SPDR Gold Shares Options',
    shortName: 'GLD Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'SLV_OPTIONS',
    symbol: 'SLV',
    name: 'iShares Silver Trust Options',
    shortName: 'SLV Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'GDX_OPTIONS',
    symbol: 'GDX',
    name: 'VanEck Gold Miners ETF Options',
    shortName: 'GDX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'USO_OPTIONS',
    symbol: 'USO',
    name: 'United States Oil Fund Options',
    shortName: 'USO Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'UNG_OPTIONS',
    symbol: 'UNG',
    name: 'United States Natural Gas Fund Options',
    shortName: 'UNG Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'DBA_OPTIONS',
    symbol: 'DBA',
    name: 'Invesco DB Agriculture ETF Options',
    shortName: 'DBA Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'DBC_OPTIONS',
    symbol: 'DBC',
    name: 'Invesco DB Commodity Index ETF Options',
    shortName: 'DBC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'UUP_OPTIONS',
    symbol: 'UUP',
    name: 'Invesco DB US Dollar Index Bullish Options',
    shortName: 'UUP Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'FXE_OPTIONS',
    symbol: 'FXE',
    name: 'CurrencyShares Euro Trust Options',
    shortName: 'FXE Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Monthly; physical'
  },
  
  {
    id: 'FXY_OPTIONS',
    symbol: 'FXY',
    name: 'CurrencyShares Japanese Yen Trust Options',
    shortName: 'FXY Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Monthly; physical'
  },
  
  {
    id: 'FXB_OPTIONS',
    symbol: 'FXB',
    name: 'CurrencyShares British Pound Sterling Trust Options',
    shortName: 'FXB Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Monthly; physical'
  },
  
  {
    id: 'FXA_OPTIONS',
    symbol: 'FXA',
    name: 'CurrencyShares Australian Dollar Trust Options',
    shortName: 'FXA Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Monthly; physical'
  },
  
  {
    id: 'FXC_OPTIONS',
    symbol: 'FXC',
    name: 'CurrencyShares Canadian Dollar Trust Options',
    shortName: 'FXC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Monthly; physical'
  },
  
  {
    id: 'FXF_OPTIONS',
    symbol: 'FXF',
    name: 'CurrencyShares Swiss Franc Trust Options',
    shortName: 'FXF Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Monthly; physical'
  },
  
  // ===== OPTIONS - SINGLE STOCK (LIQUID COHORT) =====
  
  {
    id: 'AAPL_OPTIONS',
    symbol: 'AAPL',
    name: 'Apple Inc. Options',
    shortName: 'AAPL Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Daily/weekly/monthly; high liquidity'
  },
  
  {
    id: 'MSFT_OPTIONS',
    symbol: 'MSFT',
    name: 'Microsoft Corp. Options',
    shortName: 'MSFT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; high liquidity'
  },
  
  {
    id: 'NVDA_OPTIONS',
    symbol: 'NVDA',
    name: 'NVIDIA Corp. Options',
    shortName: 'NVDA Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; high liquidity'
  },
  
  {
    id: 'AMZN_OPTIONS',
    symbol: 'AMZN',
    name: 'Amazon.com Inc. Options',
    shortName: 'AMZN Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; high liquidity'
  },
  
  {
    id: 'GOOGL_OPTIONS',
    symbol: 'GOOGL',
    name: 'Alphabet Inc. (Class A) Options',
    shortName: 'GOOGL Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; high liquidity'
  },
  
  {
    id: 'META_OPTIONS',
    symbol: 'META',
    name: 'Meta Platforms Inc. Options',
    shortName: 'META Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; high liquidity'
  },
  
  {
    id: 'TSLA_OPTIONS',
    symbol: 'TSLA',
    name: 'Tesla Inc. Options',
    shortName: 'TSLA Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Daily/weekly/monthly; high liquidity'
  },
  
  {
    id: 'AMD_OPTIONS',
    symbol: 'AMD',
    name: 'Advanced Micro Devices Options',
    shortName: 'AMD Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly'
  },
  
  {
    id: 'NFLX_OPTIONS',
    symbol: 'NFLX',
    name: 'Netflix Inc. Options',
    shortName: 'NFLX Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly'
  },
  
  {
    id: 'JPM_OPTIONS',
    symbol: 'JPM',
    name: 'JPMorgan Chase & Co. Options',
    shortName: 'JPM Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly'
  },
  
  {
    id: 'XOM_OPTIONS',
    symbol: 'XOM',
    name: 'Exxon Mobil Corp. Options',
    shortName: 'XOM Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly'
  },
  
  {
    id: 'AVGO_OPTIONS',
    symbol: 'AVGO',
    name: 'Broadcom Inc. Options',
    shortName: 'AVGO Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'us-equities',
      subAssetClass: 'equity-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly'
  },
  
  // ===== OPTIONS - FUTURES (EQUITY INDEX) =====
  
  {
    id: 'ES_OPTIONS',
    symbol: 'ES',
    name: 'E-mini S&P 500 Futures Options',
    shortName: 'ES Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 50,
    contractAsset: 'ES futures',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ES futures on exercise'
  },
  
  {
    id: 'MES_OPTIONS',
    symbol: 'MES',
    name: 'Micro E-mini S&P 500 Futures Options',
    shortName: 'MES Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 5,
    contractAsset: 'MES futures',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver MES futures; smaller notional'
  },
  
  {
    id: 'NQ_OPTIONS',
    symbol: 'NQ',
    name: 'E-mini Nasdaq-100 Futures Options',
    shortName: 'NQ Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 20,
    contractAsset: 'NQ futures',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver NQ futures'
  },
  
  {
    id: 'MNQ_OPTIONS',
    symbol: 'MNQ',
    name: 'Micro E-mini Nasdaq-100 Futures Options',
    shortName: 'MNQ Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 2,
    contractAsset: 'MNQ futures',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver MNQ futures'
  },
  
  {
    id: 'RTY_OPTIONS',
    symbol: 'RTY',
    name: 'E-mini Russell 2000 Futures Options',
    shortName: 'RTY Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 50,
    contractAsset: 'RTY futures',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver RTY futures'
  },
  
  {
    id: 'M2K_OPTIONS',
    symbol: 'M2K',
    name: 'Micro E-mini Russell 2000 Futures Options',
    shortName: 'M2K Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 5,
    contractAsset: 'M2K futures',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver M2K futures'
  },
  
  {
    id: 'YM_OPTIONS',
    symbol: 'YM',
    name: 'E-mini Dow ($5) Futures Options',
    shortName: 'YM Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'indices',
      subAssetClass: 'futures-options'
    },
    contractSize: 5,
    contractAsset: 'YM futures',
    incrementSize: 1.00,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver YM futures'
  },
  
  // ===== OPTIONS - FUTURES (RATES) =====
  
  {
    id: 'ZN_OPTIONS',
    symbol: 'ZN',
    name: '10-Year T-Note Futures Options',
    shortName: 'ZN Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'rates',
      subAssetClass: 'futures-options'
    },
    contractSize: 1000,
    contractAsset: 'Treasury futures',
    incrementSize: 0.015625,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZN futures'
  },
  
  {
    id: 'ZB_OPTIONS',
    symbol: 'ZB',
    name: '30-Year T-Bond Futures Options',
    shortName: 'ZB Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'rates',
      subAssetClass: 'futures-options'
    },
    contractSize: 1000,
    contractAsset: 'Treasury futures',
    incrementSize: 0.03125,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZB futures'
  },
  
  {
    id: 'UB_OPTIONS',
    symbol: 'UB',
    name: 'Ultra T-Bond Futures Options',
    shortName: 'UB Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'rates',
      subAssetClass: 'futures-options'
    },
    contractSize: 1000,
    contractAsset: 'Treasury futures',
    incrementSize: 0.03125,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver UB futures'
  },
  
  {
    id: 'ZF_OPTIONS',
    symbol: 'ZF',
    name: '5-Year T-Note Futures Options',
    shortName: 'ZF Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'rates',
      subAssetClass: 'futures-options'
    },
    contractSize: 1000,
    contractAsset: 'Treasury futures',
    incrementSize: 0.0078125,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZF futures'
  },
  
  {
    id: 'ZT_OPTIONS',
    symbol: 'ZT',
    name: '2-Year T-Note Futures Options',
    shortName: 'ZT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'rates',
      subAssetClass: 'futures-options'
    },
    contractSize: 2000,
    contractAsset: 'Treasury futures',
    incrementSize: 0.0078125,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZT futures'
  },
  
  {
    id: 'SR3_OPTIONS',
    symbol: 'SR3',
    name: '3-Month SOFR Futures Options',
    shortName: 'SR3 Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'rates',
      subAssetClass: 'futures-options'
    },
    contractSize: 2500,
    contractAsset: 'rate futures',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver SR3 futures'
  },
  
  // ===== OPTIONS - FUTURES (METALS) =====
  
  {
    id: 'GC_OPTIONS',
    symbol: 'GC',
    name: 'Gold Futures Options',
    shortName: 'GC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'futures-options'
    },
    contractSize: 100,
    contractAsset: 'metal futures',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver GC futures'
  },
  
  {
    id: 'SI_OPTIONS',
    symbol: 'SI',
    name: 'Silver Futures Options',
    shortName: 'SI Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'futures-options'
    },
    contractSize: 5000,
    contractAsset: 'metal futures',
    incrementSize: 0.001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver SI futures'
  },
  
  {
    id: 'HG_OPTIONS',
    symbol: 'HG',
    name: 'Copper Futures Options',
    shortName: 'HG Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'futures-options'
    },
    contractSize: 25000,
    contractAsset: 'metal futures',
    incrementSize: 0.0005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver HG futures'
  },
  
  {
    id: 'PL_OPTIONS',
    symbol: 'PL',
    name: 'Platinum Futures Options',
    shortName: 'PL Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'futures-options'
    },
    contractSize: 50,
    contractAsset: 'metal futures',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver PL futures'
  },
  
  {
    id: 'PA_OPTIONS',
    symbol: 'PA',
    name: 'Palladium Futures Options',
    shortName: 'PA Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'metals',
      subAssetClass: 'futures-options'
    },
    contractSize: 100,
    contractAsset: 'metal futures',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver PA futures'
  },
  
  // ===== OPTIONS - FUTURES (ENERGY) =====
  
  {
    id: 'CL_OPTIONS',
    symbol: 'CL',
    name: 'WTI Crude Oil Futures Options',
    shortName: 'CL Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'futures-options'
    },
    contractSize: 1000,
    contractAsset: 'energy futures',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver CL futures'
  },
  
  {
    id: 'NG_OPTIONS',
    symbol: 'NG',
    name: 'Henry Hub Natural Gas Futures Options',
    shortName: 'NG Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'futures-options'
    },
    contractSize: 10000,
    contractAsset: 'energy futures',
    incrementSize: 0.001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver NG futures'
  },
  
  {
    id: 'RB_OPTIONS',
    symbol: 'RB',
    name: 'RBOB Gasoline Futures Options',
    shortName: 'RB Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'futures-options'
    },
    contractSize: 42000,
    contractAsset: 'energy futures',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver RB futures'
  },
  
  {
    id: 'HO_OPTIONS',
    symbol: 'HO',
    name: 'Heating Oil Futures Options',
    shortName: 'HO Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'futures-options'
    },
    contractSize: 42000,
    contractAsset: 'energy futures',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver HO futures'
  },
  
  {
    id: 'BRN_OPTIONS',
    symbol: 'BRN',
    name: 'ICE Brent Crude Futures Options',
    shortName: 'BRN Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'energies',
      subAssetClass: 'futures-options'
    },
    contractSize: 1000,
    contractAsset: 'energy futures',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'ICE Europe; deliver Brent futures'
  },
  
  // ===== OPTIONS - FUTURES (AGRICULTURE) =====
  
  {
    id: 'ZC_OPTIONS',
    symbol: 'ZC',
    name: 'Corn Futures Options',
    shortName: 'ZC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 5000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZC futures'
  },
  
  {
    id: 'ZW_OPTIONS',
    symbol: 'ZW',
    name: 'Chicago SRW Wheat Futures Options',
    shortName: 'ZW Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 5000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZW futures'
  },
  
  {
    id: 'KE_OPTIONS',
    symbol: 'KE',
    name: 'KC HRW Wheat Futures Options',
    shortName: 'KE Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 5000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver KE futures'
  },
  
  {
    id: 'ZS_OPTIONS',
    symbol: 'ZS',
    name: 'Soybean Futures Options',
    shortName: 'ZS Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 5000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZS futures'
  },
  
  {
    id: 'ZM_OPTIONS',
    symbol: 'ZM',
    name: 'Soybean Meal Futures Options',
    shortName: 'ZM Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 100,
    contractAsset: 'commodity futures',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZM futures'
  },
  
  {
    id: 'ZL_OPTIONS',
    symbol: 'ZL',
    name: 'Soybean Oil Futures Options',
    shortName: 'ZL Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 60000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZL futures'
  },
  
  {
    id: 'ZO_OPTIONS',
    symbol: 'ZO',
    name: 'Oats Futures Options',
    shortName: 'ZO Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 5000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZO futures'
  },
  
  {
    id: 'ZR_OPTIONS',
    symbol: 'ZR',
    name: 'Rough Rice Futures Options',
    shortName: 'ZR Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 2000,
    contractAsset: 'commodity futures',
    incrementSize: 0.005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver ZR futures'
  },
  
  {
    id: 'KC_OPTIONS',
    symbol: 'KC',
    name: 'Coffee "C" Futures Options (ICE)',
    shortName: 'KC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 37500,
    contractAsset: 'commodity futures',
    incrementSize: 0.0005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'ICE US; deliver KC futures'
  },
  
  {
    id: 'SB_OPTIONS',
    symbol: 'SB',
    name: 'Sugar No.11 Futures Options (ICE)',
    shortName: 'SB Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 112000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'ICE US; deliver SB futures'
  },
  
  {
    id: 'CC_OPTIONS',
    symbol: 'CC',
    name: 'Cocoa Futures Options (ICE)',
    shortName: 'CC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 10,
    contractAsset: 'commodity futures',
    incrementSize: 1.00,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'ICE US; deliver CC futures'
  },
  
  {
    id: 'CT_OPTIONS',
    symbol: 'CT',
    name: 'Cotton No.2 Futures Options (ICE)',
    shortName: 'CT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 50000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'ICE US; deliver CT futures'
  },
  
  {
    id: 'OJ_OPTIONS',
    symbol: 'OJ',
    name: 'Frozen Concentrated Orange Juice Options (ICE)',
    shortName: 'OJ Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'commodities',
      subAssetClass: 'futures-options'
    },
    contractSize: 15000,
    contractAsset: 'commodity futures',
    incrementSize: 0.0005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'ICE US; deliver OJ futures'
  },
  
  // ===== OPTIONS - FUTURES (FX) =====
  
  {
    id: '6E_OPTIONS',
    symbol: '6E',
    name: 'Euro FX Futures Options',
    shortName: '6E Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 125000,
    contractAsset: 'currency futures',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6E futures'
  },
  
  {
    id: '6J_OPTIONS',
    symbol: '6J',
    name: 'Japanese Yen Futures Options',
    shortName: '6J Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 12500000,
    contractAsset: 'currency futures',
    incrementSize: 0.0000001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6J futures'
  },
  
  {
    id: '6A_OPTIONS',
    symbol: '6A',
    name: 'Australian Dollar Futures Options',
    shortName: '6A Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 100000,
    contractAsset: 'currency futures',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6A futures'
  },
  
  {
    id: '6B_OPTIONS',
    symbol: '6B',
    name: 'British Pound Futures Options',
    shortName: '6B Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 62500,
    contractAsset: 'currency futures',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6B futures'
  },
  
  {
    id: '6C_OPTIONS',
    symbol: '6C',
    name: 'Canadian Dollar Futures Options',
    shortName: '6C Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 100000,
    contractAsset: 'currency futures',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6C futures'
  },
  
  {
    id: '6N_OPTIONS',
    symbol: '6N',
    name: 'New Zealand Dollar Futures Options',
    shortName: '6N Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 100000,
    contractAsset: 'currency futures',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6N futures'
  },
  
  {
    id: '6M_OPTIONS',
    symbol: '6M',
    name: 'Mexican Peso Futures Options',
    shortName: '6M Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 500000,
    contractAsset: 'currency futures',
    incrementSize: 0.000025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6M futures'
  },
  
  {
    id: '6S_OPTIONS',
    symbol: '6S',
    name: 'Swiss Franc Futures Options',
    shortName: '6S Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'currencies',
      subAssetClass: 'futures-options'
    },
    contractSize: 125000,
    contractAsset: 'currency futures',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver 6S futures'
  },
  
  // ===== OPTIONS - FUTURES (CRYPTO) =====
  
  {
    id: 'BTC_OPTIONS',
    symbol: 'BTC',
    name: 'Bitcoin Futures Options',
    shortName: 'BTC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'digital-assets',
      subAssetClass: 'futures-options'
    },
    contractSize: 5,
    contractAsset: 'BTC futures',
    incrementSize: 5.00,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver CME Bitcoin futures'
  },
  
  {
    id: 'MBT_OPTIONS',
    symbol: 'MBT',
    name: 'Micro Bitcoin Futures Options',
    shortName: 'MBT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'digital-assets',
      subAssetClass: 'futures-options'
    },
    contractSize: 0.10,
    contractAsset: 'BTC futures',
    incrementSize: 5.00,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver CME Micro Bitcoin futures'
  },
  
  {
    id: 'ETH_OPTIONS',
    symbol: 'ETH',
    name: 'Ether Futures Options',
    shortName: 'ETH Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'digital-assets',
      subAssetClass: 'futures-options'
    },
    contractSize: 50,
    contractAsset: 'ETH futures',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver CME Ether futures'
  },
  
  {
    id: 'MET_OPTIONS',
    symbol: 'MET',
    name: 'Micro Ether Futures Options',
    shortName: 'MET Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'digital-assets',
      subAssetClass: 'futures-options'
    },
    contractSize: 0.10,
    contractAsset: 'ETH futures',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Deliver CME Micro Ether futures'
  },
  
  // ===== OPTIONS - SPOT BITCOIN ETFs =====
  
  {
    id: 'IBIT_OPTIONS',
    symbol: 'IBIT',
    name: 'iShares Bitcoin Trust Options',
    shortName: 'IBIT Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'digital-assets',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  },
  
  {
    id: 'FBTC_OPTIONS',
    symbol: 'FBTC',
    name: 'Fidelity Wise Origin Bitcoin Fund Options',
    shortName: 'FBTC Options',
    classification: {
      assetClass: 'options',
      marketSegment: 'digital-assets',
      subAssetClass: 'etf-options'
    },
    contractSize: 100,
    contractAsset: 'shares',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Weekly/monthly; physical'
  }
];